import { IColor, IViewer } from '@inter-ikea-betrakta/viewer';
import { ComponentIds, Entity, ViewerObjectComponent } from '@inter-ikea-core/core';
import { captureException } from '@sentry/react';
import { CustomOutlineComponent } from 'CoreAPI/Components/CustomOutlineComponent';
import { SkyttaComponentIds } from 'CoreAPI/Components/SkyttaComponentIds';
import CoreAPI from 'CoreAPI/coreApi';
import { getCamera, getViewer } from 'CoreAPI/viewerSetup';
import store from 'redux-store/store';
import { constantCustomizeMenuWidth } from 'utils/constants';
import { OutlineColor } from 'utils/constants/colors';
import { nonNullable } from 'utils/nonNullable';
import { remToPixels } from 'utils/remToPixels';

const mapModelUris = (catalogData: any) => {
  return [
    ...new Set(
      catalogData.products
        .map((product: { modelURI: string }) => {
          return product.modelURI;
        })
        .filter(Boolean)
    )
  ];
};

export const preloadAllModels = async () => {
  const { skyttaCatalogData, paxPanelsCatalogData } = store.getState().configuration;
  const combineModelArrays = [
    ...mapModelUris(skyttaCatalogData),
    ...mapModelUris(paxPanelsCatalogData)
  ] as string[];
  return getViewer()?.persistent_load_models(combineModelArrays);
};

export const getViewerId = (entity: Entity) => {
  return entity
    .getComponent<ViewerObjectComponent>(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
    ?.viewerObject.getViewerObjectId();
};

const hasEnteredObject = async (
  event: MouseEvent,
  viewerId: number,
  viewer: IViewer,
  isRtl: boolean
) => {
  const ltlMousePosX = event.clientX;
  const rtlMousePosX = ltlMousePosX - remToPixels(constantCustomizeMenuWidth);
  const mousePosX = isRtl ? rtlMousePosX : ltlMousePosX;
  const mousePosY = event.clientY;
  const bounds = await viewer.get_object_bounds_world(viewerId);
  const { max, min } = bounds;

  const canvasBoundsMax = viewer.project_world_position_to_canvas(max.x, max.y, max.z);
  const canvasBoundsMin = viewer.project_world_position_to_canvas(min.x, min.y, min.z);

  let enteredObject = false;

  const isCameraBehindDoors = getCamera().position.z < 0;

  if (canvasBoundsMax && canvasBoundsMin) {
    const enteredObjectX = isCameraBehindDoors
      ? mousePosX < canvasBoundsMin.x && mousePosX > canvasBoundsMax.x
      : mousePosX < canvasBoundsMax.x && mousePosX > canvasBoundsMin.x;
    const enteredObjectY = mousePosY > canvasBoundsMax.y && mousePosY < canvasBoundsMin.y;
    enteredObject = enteredObjectX && enteredObjectY;
  }

  return enteredObject;
};

/**
 * Since the objects is coordinates, we can get multiple objects hovered at the same time. For example when two doors overlaps.
 */
const isCurrentlyHovered = async (
  event: MouseEvent,
  viewerIds: number[],
  viewer: IViewer,
  isRtl: boolean
) => {
  const currentlyHovered = await Promise.all(
    viewerIds.map(async value => {
      const parentId = viewer.get_parent(value);
      if (parentId && (await hasEnteredObject(event, parentId, viewer, isRtl))) {
        return value;
      }
      return null;
    })
  );
  const filteredCurrentlyHovered = currentlyHovered.filter(nonNullable);
  return filteredCurrentlyHovered;
};

/**
 * Get closest object
 */
const isClosestObjectByDepth = (viewerIds: number[], viewer: IViewer) => {
  const outlineObjectsByDepth = viewerIds
    .map((value: number) => {
      const parentId = viewer.get_parent(value);

      if (!parentId) {
        captureException(new Error(`Failed to get viewerId from isClosestObjectByDepth`));
        return null;
      }

      const position = viewer.get_position(parentId);
      const depth = position.z;

      return { id: value, depth };
    })
    .filter(nonNullable);

  const closestObject = Math.max(
    ...outlineObjectsByDepth.map(value => {
      return value.depth;
    })
  );

  return outlineObjectsByDepth.filter(value => {
    return value.depth === closestObject;
  })[0];
};

const isSelect = (currentColor: IColor) => {
  const { select } = OutlineColor;
  return select.r === currentColor.r && select.g === currentColor.g && select.b === currentColor.b;
};

/**
 * This checks if you clicked outside our objects, eg. when no object is clicked.
 */
const clickedOutsideAllObjects = async (
  event: MouseEvent,
  viewer: IViewer,
  outlineObjectIds: number[],
  isRtl: boolean
) => {
  const hasEnteredAnyObject = Promise.all(
    outlineObjectIds.map(async value => {
      const clickedInside = await hasEnteredObject(event, value, viewer, isRtl);
      return clickedInside;
    })
  );

  const clickedInsideAnyObject = (await hasEnteredAnyObject).some(value => {
    return value;
  });

  return !clickedInsideAnyObject;
};

const setOutlineColor = (viewer: IViewer, id: number) => {
  const closestObjectCurrentColor = viewer.get_object_outline_color(id);

  viewer.set_outline(id, true, false);

  if (isSelect(closestObjectCurrentColor)) {
    viewer.set_object_outline_color(id, OutlineColor.select);
  }

  return viewer.get_parent(id);
};

export const isModelHovered = async (
  event: MouseEvent,
  outlineId: number,
  viewer: IViewer,
  outlineObjectIds: number[],
  coreApi: CoreAPI,
  isRtl: boolean
) => {
  if (await clickedOutsideAllObjects(event, viewer, outlineObjectIds, isRtl)) {
    document.body.classList.remove('hovered');
  } else {
    document.body.classList.add('hovered');
  }

  const currentColor = viewer.get_object_outline_color(outlineId);

  const hoveredObjects = await isCurrentlyHovered(event, outlineObjectIds, viewer, isRtl);

  const draggedDoorOutlineId = coreApi.core.services.entityManager.entities
    .find(entity => {
      return entity.hasComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID);
    })
    ?.getComponent<CustomOutlineComponent>(SkyttaComponentIds.CustomOutlineComponentId)?.viewerId;

  if (draggedDoorOutlineId) {
    return setOutlineColor(viewer, draggedDoorOutlineId);
  }

  const isClosestObjectHovered = isClosestObjectByDepth(hoveredObjects, viewer);

  if (isClosestObjectHovered && isClosestObjectHovered.id === outlineId) {
    return setOutlineColor(viewer, outlineId);
  }
  if (isSelect(currentColor)) {
    return false;
  }

  viewer.set_outline(outlineId, false, false);
  return false;
};
